// web_components/wcScrollingBar/wcScrollingBar.ts

import { WebComponent_Base } from '../WebComponent_Base';
import { addEventListener, removeEventListener } from '../../utils/events';
import { WebComponent_Utils } from '../WebComponent_Utils';
import { renderProductHTML } from './wcScrollingBar_Renderer';
import { toggleClass } from '../../utils/toggleClass';

/**
 * A custom web component for creating a scrolling bar.
 */
export class WcScrollingBar extends WebComponent_Base {
	public dataAttributes: any;
	public instanceId: string;
	private handleKeydown!: (event: Event) => void;

	constructor() {
		super();
		this.dataAttributes = {};
		this.instanceId = WebComponent_Utils.generateUniqueId();
		this.handleKeydown = this.handleKeydownEvent.bind(this);
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcScrollingBar';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {
		this.getData();
	}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {
		toggleClass([document.body], ['has-scrolling-bar']);
	}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Transfers data attributes from the light DOM elements to this.dataAttributes.
	 * This includes data-css for styling, store ids, product type, variables etc.
	 */
	private getData(): any {
		// Variables
		const variables = this.getAttribute('data-variables');
		if (variables) {
			try {
				this.dataAttributes['variables'] = JSON.parse(variables);
			} catch (e) {
				console.error(
					'wcScrollingBar Component: Invalid JSON in data-variables attribute:',
					e,
				);
				this.dataAttributes['variables'] = null;
			}
		}

		// CSS
		const css = this.getAttribute('data-css');
		if (css) {
			try {
				this.dataAttributes['css'] = JSON.parse(css);
			} catch (e) {
				console.error('wcScrollingBar Component: Invalid CSS in data-css attribute:', e);
				this.dataAttributes['css'] = null;
			}
		}
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.dataAttributes, this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Adds event listeners for the CTA button.
	 */
	protected attachListeners(): void {
		const ctaBtn = this.shadowRoot!.querySelector('.wc-scrolling-bar__cta');

		// Add click event listeners for the buttons
		if (ctaBtn) {
			addEventListener(ctaBtn, 'click', this.scrollToTarget.bind(this));
			addEventListener(window, 'scroll', this.showScrollingBar.bind(this));

			if (document.readyState === 'loading') {
				this.showScrollingBar.bind(this);
			} else {
				this.showScrollingBar();
			}
		}

		// Add the keydown event listener for "Escape" key
		addEventListener(document, 'keydown', this.handleKeydown);
	}

	/**
	 * Handles the 'keydown' event to listen for the "Escape" key.
	 * When the "Escape" key is pressed, it invokes the closeScrollingBar function.
	 *
	 * @param {Event} event - The event object, cast as a KeyboardEvent to access key properties.
	 */
	handleKeydownEvent(event: Event) {
		if ((event as KeyboardEvent).key === 'Escape') {
			event.stopPropagation();
			this.scrollToTarget();
		}
	}

	/**
	 * Show the scrolling bar if the page scroll point precedes or exceeds the element's position on the page.
	 */
	showScrollingBar() {
		const scrollingBar =
			(this.shadowRoot!.querySelector('.wc-scrolling-bar') as HTMLElement) || null;
		if (!scrollingBar) return;

		const scrollTarget = scrollingBar.dataset.scrollTo || null;
		if (!scrollTarget) return;

		const targetElement = document.querySelector(scrollTarget) as HTMLElement;
		if (!targetElement) return;

		const spaceBetweenTarget = window.innerHeight / 2 || null;
		if (!spaceBetweenTarget) return;

		if (
			targetElement.getBoundingClientRect().top > spaceBetweenTarget ||
			targetElement.getBoundingClientRect().bottom < spaceBetweenTarget
		) {
			if (!scrollingBar.classList.contains('active')) {
				scrollingBar.classList.add('active');
			}
		} else {
			if (scrollingBar.classList.contains('active')) {
				scrollingBar.classList.remove('active');
			}
		}
	}

	/**
	 * Scroll to the target element.
	 */
	scrollToTarget() {
		const scrollingBar = this.shadowRoot!.querySelector('.wc-scrolling-bar') as HTMLElement;
		if (!scrollingBar) return;

		const scrollTarget = scrollingBar.dataset.scrollTo || null;
		if (!scrollTarget) return;

		const targetElement = document.querySelector(scrollTarget) as HTMLElement;
		if (targetElement) {
			targetElement.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}
		removeEventListener(document, 'keydown', this.handleKeydown);
	}

	/**
	 * Applies visibility settings to all elements within the shadow DOM based content.
	 * This ensures elements are shown or hidden according to the presence of variable data
	 * and the component settings file.
	 */
	protected conditionalVisibility(): void {
		WebComponent_Utils.applyVisibilitySettings(<ShadowRoot>this.shadowRoot);
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-scrolling-bar', WcScrollingBar);
